/**
 * Enregistrement DIFFÉRÉ des contrôleurs Stimulus lourds et propres à une surface.
 *
 * `controllers/index.js` reste la liste écrite à la main de TOUS les contrôleurs. Un contrôleur
 * différé y est déclaré par `registerLazy('gantt', () => import('./gantt_controller'))` au lieu
 * de `import` + `application.register` : esbuild (`--splitting`) le sort du bundle initial dans
 * son propre chunk, que le navigateur ne télécharge qu'au premier élément `data-controller="gantt"`
 * de la page.
 *
 * Le registre observe le document, présent ET à venir : navigation Turbo, Turbo Stream, frame,
 * attribut `data-controller` posé par un autre contrôleur. Le module reçu, `application.register`
 * connecte Stimulus à tous les éléments déjà là : le contrôleur se comporte ensuite exactement
 * comme un contrôleur enregistré d'emblée.
 *
 * ⚠️ Ce qui change : son `connect()` arrive APRÈS le premier rendu (un aller-retour réseau à la
 * première visite, ensuite le cache immuable de `/assets/`), et dans un ordre quelconque par
 * rapport aux autres contrôleurs différés. Ne JAMAIS différer :
 *   • un contrôleur qu'un autre lit par `application.getControllerForElementAndIdentifier` ou
 *     par un outlet non gardé (`this.xOutlet` sans `hasXOutlet`) — il le trouverait absent
 *     (`modal`, `slide-over`, `searchable-select`, `amount-validator`, `filter-pills`…) ;
 *   • un contrôleur qui écoute un événement émis au `connect()` d'un autre (`advance-warning`
 *     ← `invoice-lines-table`), ou qui en émet un à son `connect()` (`annotation-panel`) ;
 *   • un contrôleur qui reçoit un événement d'un champ de formulaire (input, change, blur, paste,
 *     keydown, submit) pour en écrire, renommer, enregistrer ou envoyer une valeur : la saisie
 *     faite avant son `connect()` est perdue en silence (#875 : SIRET envoyé vide à la création
 *     d'une entreprise, `sirene-lookup`) ;
 *   • un contrôleur dont l'action débloque le geste principal de la page : le clic perdu laisse
 *     la page bloquée (#875 : zone de dépôt grisée après « Déposer une facture »,
 *     `upload-type-selector`) ;
 *   • le chrome de l'app (sidebar, menus, modales génériques, flash, infobulles) ;
 *   • un contrôleur dont un test épingle `application.register('x', XController)` dans index.js.
 *
 * Ne se diffère donc qu'un contrôleur d'affichage : un geste perdu ne laisse qu'un affichage en
 * retard, corrigé au geste suivant ou à sa connexion. La liste blanche et la raison de chacun
 * vivent dans `test/javascript/build/initial_bundle_budget.test.mjs` (`LAZY_ALLOWED`) : un
 * `registerLazy` qui n'y figure pas fait rougir le test.
 *
 * Un chunk introuvable (déploiement entre deux navigations sans rechargement) est signalé en
 * console et retenté au prochain élément qui porte l'identifiant, jamais en boucle.
 *
 * Module pur (aucun import) : `test/javascript/utils/lazy_controller_registry.test.mjs` lui passe
 * un faux document et un faux `MutationObserver`.
 */
export function lazyControllerRegistry(
  application,
  { root = document.documentElement, Observer = MutationObserver, logger = console } = {}
) {
  const attribute = application.schema.controllerAttribute;
  const loaders = new Map();
  let idle = 0; // entrées encore à charger : à 0, une mutation du DOM ne coûte plus aucun balayage
  let observing = false;
  let scanScheduled = false;

  function loadFor(element) {
    const tokens = element.getAttribute(attribute);
    if (!tokens) return;
    for (const identifier of tokens.split(/\s+/)) {
      const entry = loaders.get(identifier);
      if (!entry || entry.state !== 'idle') continue;
      entry.state = 'loading';
      idle -= 1;
      Promise.resolve()
        .then(entry.loader)
        .then((module) => {
          entry.state = 'loaded';
          application.register(identifier, module.default);
        })
        .catch((error) => {
          entry.state = 'idle';
          idle += 1;
          logger.error(
            `Contrôleur Stimulus « ${identifier} » : chargement différé impossible`,
            error
          );
        });
    }
  }

  function scan(node) {
    if (node.hasAttribute(attribute)) loadFor(node);
    for (const element of node.querySelectorAll(`[${attribute}]`)) loadFor(element);
  }

  // Une seule passe pour toutes les déclarations d'index.js, qui s'enchaînent dans la même tâche.
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    queueMicrotask(() => {
      scanScheduled = false;
      scan(root);
    });
  }

  function observe() {
    new Observer((records) => {
      if (idle === 0) return;
      for (const record of records) {
        if (record.type === 'attributes') {
          loadFor(record.target);
        } else {
          for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
        }
      }
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [attribute],
    });
    observing = true;
  }

  return function registerLazy(identifier, loader) {
    if (loaders.has(identifier))
      throw new Error(`registerLazy : « ${identifier} » déclaré deux fois`);
    loaders.set(identifier, { loader, state: 'idle' });
    idle += 1;
    if (!observing) observe();
    scheduleScan();
  };
}
